import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Ticket as TicketType } from '../types/index.ts';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Ticket, QrCode, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function MyTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('upcoming');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      const data = await api.getMyTickets();
      setTickets(data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const upcomingTickets = tickets.filter(
    (ticket) => ticket.event && new Date(ticket.event.date) > new Date()
  );

  const pastTickets = tickets.filter(
    (ticket) => ticket.event && new Date(ticket.event.date) <= new Date()
  );

  const renderTickets = (ticketList: TicketType[]) => {
    if (ticketList.length === 0) {
      return (
        <Card className="glass border-white/10 p-12 text-center">
          <Ticket className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">No tickets found</p>
          <Button onClick={() => navigate('/discover')} className="shadow-glow">
            Discover Events
          </Button>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {ticketList.map((ticket) => (
          <TicketCard key={ticket.ticket_id} ticket={ticket} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
          My Tickets
        </h1>
        <p className="text-lg text-gray-400">
          View and manage your event tickets
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="glass border-white/10">
          <TabsTrigger value="upcoming">
            Upcoming ({upcomingTickets.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            Past ({pastTickets.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            renderTickets(upcomingTickets)
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            renderTickets(pastTickets)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TicketCard({ ticket }: { ticket: TicketType }) {
  const [showQR, setShowQR] = useState(false);
  const navigate = useNavigate();

  if (!ticket.event) return null;

  const isPast = new Date(ticket.event.date) <= new Date();

  return (
    <Card className="glass border-white/10 overflow-hidden">
      {/* Event Image */}
      <div className="relative h-32 bg-background-subtle">
        {ticket.event.poster_url || ticket.event.image ? (
          <img
            src={ticket.event.poster_url || ticket.event.image}
            alt={ticket.event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="h-12 w-12 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background-paper to-transparent" />
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          {ticket.scanned ? (
            <Badge variant="default" className="bg-green-500">Scanned</Badge>
          ) : isPast ? (
            <Badge variant="secondary">Past Event</Badge>
          ) : (
            <Badge variant="default" className="bg-primary">Valid</Badge>
          )}
        </div>
      </div>

      <CardContent className="p-5 space-y-4">
        {/* Event Info */}
        <div className="space-y-2">
          <h3 className="font-heading font-bold text-lg line-clamp-1">
            {ticket.event.title}
          </h3>
          <p className="text-sm text-gray-400">{ticket.event.artist}</p>
        </div>

        {/* Date & Location */}
        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(ticket.event.date), 'MMM dd, yyyy • h:mm a')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4" />
            <span className="line-clamp-1">{ticket.event.venue}</span>
          </div>
        </div>

        {/* Ticket Details */}
        <div className="pt-3 border-t border-white/10 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Ticket Type</span>
            <span className="font-medium">{ticket.ticket_type}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Price Paid</span>
            <span className="font-medium">
              {ticket.currency || '$'}{ticket.price}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Purchase Date</span>
            <span className="font-medium">
              {format(new Date(ticket.purchase_date), 'MMM dd, yyyy')}
            </span>
          </div>
        </div>

        {/* QR Code */}
        {!isPast && (
          <Button
            onClick={() => setShowQR(!showQR)}
            variant={showQR ? 'secondary' : 'default'}
            className="w-full"
          >
            <QrCode className="h-4 w-4 mr-2" />
            {showQR ? 'Hide QR Code' : 'Show QR Code'}
          </Button>
        )}

        {showQR && ticket.qr_code && (
          <div className="bg-white p-4 rounded-lg">
            <img
              src={ticket.qr_code}
              alt="Ticket QR Code"
              className="w-full h-auto"
            />
            <p className="text-center text-xs text-gray-600 mt-2">
              Show this QR code at the venue entrance
            </p>
          </div>
        )}

        {/* View Event Button */}
        <Button
          onClick={() => navigate(`/event/${ticket.event_id}`)}
          variant="outline"
          className="w-full glass-hover"
        >
          View Event Details
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
